import React from 'react';
import { inputStyle, labelStyle, helpStyle, widthStyle, daysFromToday } from './quoteStyles';

const describeDays = (days) => {
  if (days < 0) {
    const n = Math.abs(days);
    return `${n} day${n === 1 ? '' : 's'} ago - please double-check this date`;
  }
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow (1 day from today)';
  return `${days} days from today`;
};

const hintColor = (days) => {
  if (days < 0) return '#c53030';
  if (days <= 3) return '#b7791f';
  return 'var(--brand-primary)';
};

const DaysHint = ({ value }) => {
  const days = daysFromToday(value);
  if (days === null || Number.isNaN(days)) return null;
  return (
    <p data-testid="days-from-today-hint" style={{ ...helpStyle, color: hintColor(days), fontStyle: 'normal', fontWeight: '600' }}>
      {describeDays(days)}
    </p>
  );
};

const RadioGroup = ({ field, value, onChange, testId }) => (
  <div role="radiogroup" data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
    {field.options.map((opt) => (
      <label key={opt} style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.7rem 1rem', cursor: 'pointer',
        border: `1px solid ${value === opt ? 'var(--brand-primary)' : 'var(--border-medium)'}`,
        backgroundColor: value === opt ? 'rgba(44, 90, 160, 0.06)' : 'var(--bg-card)', borderRadius: '8px',
        transition: 'border-color 0.15s, background-color 0.15s'
      }}>
        <input type="radio" name={testId} value={opt} checked={value === opt} onChange={() => onChange(opt)} style={{ accentColor: 'var(--brand-primary)' }} />
        <span style={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>{opt}</span>
      </label>
    ))}
  </div>
);

export const QuoteField = ({ field, value, onChange, idPrefix = '' }) => {
  const testId = `${idPrefix}${field.key}`;
  const common = { id: testId, 'data-testid': testId, name: testId, required: !!field.required, style: inputStyle };
  const v = value ?? '';

  let control;
  switch (field.type) {
    case 'textarea':
      control = <textarea {...common} rows={field.rows || 3} value={v} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />;
      break;
    case 'select':
      control = (
        <select {...common} value={v} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select...</option>
          {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
      break;
    case 'radio':
      control = <RadioGroup field={field} value={v} onChange={onChange} testId={testId} />;
      break;
    case 'number':
      control = <input {...common} type="number" min={field.min} max={field.max} value={v} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'date':
      control = <input {...common} type="date" value={v} onChange={(e) => onChange(e.target.value)} />;
      break;
    default:
      control = <input {...common} type={field.type} value={v} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} />;
  }

  return (
    <div style={widthStyle(field.width)}>
      <label htmlFor={testId} style={labelStyle}>
        {field.label}{field.required && <span style={{ color: '#c53030' }}> *</span>}
      </label>
      {control}
      {field.type === 'date' && field.daysFromToday && <DaysHint value={v} />}
      {field.help && <p style={helpStyle}>{field.help}</p>}
    </div>
  );
};

export const QuoteRepeater = ({ field, items, onChange, idPrefix = '' }) => (
  <div style={{ gridColumn: 'span 12', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
    {items.map((item, i) => (
      <div key={i} data-testid={`${idPrefix}${field.key}-${i}`} style={{
        padding: '1.5rem', borderRadius: '10px', border: '1px dashed var(--brand-primary)', backgroundColor: 'rgba(44, 90, 160, 0.03)'
      }}>
        <h4 style={{ margin: '0 0 1rem', color: 'var(--brand-primary)', fontSize: '1rem', fontWeight: '700', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
          {field.label} {i + 1}
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '1rem' }} className="quote-grid">
          {field.fields.map((sub) => (
            <QuoteField key={sub.key} field={sub} value={item[sub.key] ?? sub.default ?? ''} idPrefix={`${idPrefix}${field.key}-${i}-`}
              onChange={(val) => onChange(items.map((it, j) => (j === i ? { ...it, [sub.key]: val } : it)))} />
          ))}
        </div>
      </div>
    ))}
  </div>
);
